"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3,
  Clock,
  Trophy,
  LayoutGrid,
  Map as MapIcon,
  List,
  Users,
  Vote,
  ArrowLeft,
} from "lucide-react";
import { formatDate, isHlasovanieAktivne, cn } from "@/lib/utils";
import type {
  Hlasovanie,
  VysledokSlotu,
  AgregovanyVysledok,
  StatistikyZostavy as StatistikyZostavyType,
} from "@/types/database";
import type { User } from "@supabase/supabase-js";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { useEffect, useState, useMemo } from "react";
// import { CompleteRoster } from "./complete-roster";
import { RosterFormation } from "./roster-formation";
import { RosterRink } from "./roster-rink";
import { VysledkyHeader } from "./vysledky-header";
import { SlotsView } from "./slots-view";
import { AnalysisView } from "./analysis-view";
import { AgregatedRoster } from "./agregated-roster";

interface VysledkyContentProps {
  hlasovania: Hlasovanie[];
  user: User | null;
}

interface VysledkyData {
  vysledky: VysledokSlotu[];
  pocet_hlasov: number;
  pocet_registrovanych: number;
}

interface AgregovaneData {
  agregovane_vysledky: AgregovanyVysledok[];
  pocet_registrovanych: number;
}

type ViewType = "formations" | "rink" | "slots" | "roster" | "analysis";

const containerAnimation = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemAnimation = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

const EMPTY_STATE_CARD_CLASSES =
  "border-dashed bg-linear-to-br from-background to-primary/5 dark:to-primary/10";
const EMPTY_STATE_ICON_CONTAINER =
  "w-16 h-16 bg-linear-to-br from-brand-gradient-from to-brand-gradient-to rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg";
const VIEW_SELECTOR_CONTAINER_CLASSES =
  "inline-flex p-1 bg-muted/50 rounded-xl border border-muted/60";
const VIEW_BUTTON_CLASSES = "rounded-lg gap-2";

export function VysledkyContent({ hlasovania, user }: VysledkyContentProps) {
  const [vysledkyData, setVysledkyData] = useState<VysledkyData | null>(null);
  const [agregovaneData, setAgregovaneData] = useState<AgregovaneData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewType, setViewType] = useState<ViewType>("formations");
  const [pocetRegistrovanych, setPocetRegistrovanych] = useState<number>(0);

  // Agregácia výsledkov - každý hráč len raz so súčtom hlasov
  const agregovaneVysledky = useMemo(() => {
    if (!vysledkyData) return [];

    const hraciMap = new Map<string, VysledokSlotu>();

    vysledkyData.vysledky.forEach((vysledok) => {
      const existujuci = hraciMap.get(vysledok.hrac_id);

      if (existujuci) {
        // Ak hráč už existuje, pripočítame hlasy
        existujuci.pocet_hlasov += vysledok.pocet_hlasov;
      } else {
        // Vytvoríme nový záznam (kópia objektu)
        hraciMap.set(vysledok.hrac_id, { ...vysledok });
      }
    });

    return Array.from(hraciMap.values());
  }, [vysledkyData]);

  const zoskupeneVysledky = useMemo(() => {
    if (!vysledkyData) return {};
    return (vysledkyData.vysledky || []).reduce(
      (acc, curr) => {
        const pos = curr.pozicia;
        const slotNum = curr.slot;
        if (!acc[pos]) acc[pos] = {};
        if (!acc[pos][slotNum]) acc[pos][slotNum] = [];
        acc[pos][slotNum].push(curr);
        return acc;
      },
      {} as Record<string, Record<number, VysledokSlotu[]>>,
    );
  }, [vysledkyData]);

  const analyzaStats = useMemo<StatistikyZostavyType | null>(() => {
    if (!zoskupeneVysledky || Object.keys(zoskupeneVysledky).length === 0)
      return null;
    const topHraci = Object.values(zoskupeneVysledky)
      .flatMap((p) => Object.values(p).map((hraci) => hraci[0]))
      .filter(Boolean);

    if (topHraci.length === 0) return null;

    const pocet = topHraci.length;

    const sumaZapasy = topHraci.reduce(
      (acc, h) => acc + (h.sezona_zapasy || 0),
      0,
    );
    const sumaGoly = topHraci.reduce((acc, h) => acc + (h.sezona_goly || 0), 0);
    const sumaAsistencie = topHraci.reduce(
      (acc, h) => acc + (h.sezona_asistencie || 0),
      0,
    );
    const sumaBody = topHraci.reduce((acc, h) => acc + (h.sezona_body || 0), 0);
    const sumaPrijmov = topHraci.reduce((acc, h) => acc + (h.prijem || 0), 0);
    const sumaHodnota = topHraci.reduce(
      (acc, h) => acc + (h.hodnota_hraca || 0),
      0,
    );

    const veky = topHraci
      .map((h) => {
        if (h.vek_k_ms) return h.vek_k_ms;
        if (!h.datum_narodenia) return null;
        const birth = new Date(h.datum_narodenia);
        const zoh = new Date("2026-02-06");
        return zoh.getFullYear() - birth.getFullYear();
      })
      .filter((v): v is number => v !== null);

    const priemernyVek =
      veky.length > 0 ? veky.reduce((a, b) => a + b, 0) / veky.length : 0;

    return {
      priemerny_vek: priemernyVek,
      najmladsi_hrac_meno: null,
      najmladsi_hrac_vek: veky.length > 0 ? Math.min(...veky) : null,
      najstarsi_hrac_meno: null,
      najstarsi_hrac_vek: veky.length > 0 ? Math.max(...veky) : null,
      suma_prijmov: sumaPrijmov,
      suma_hodnota_hracov: sumaHodnota,
      pocet_hracov: pocet,
      suma_zapasy: sumaZapasy,
      suma_goly: sumaGoly,
      suma_asistencie: sumaAsistencie,
      suma_body: sumaBody,
    };
  }, [zoskupeneVysledky]);

  // Samostatné načítanie počtu registrovaných používateľov
  useEffect(() => {
    let mounted = true;

    const nacitajPocetRegistrovanych = async () => {
      const supabase = createClient();

      try {
        console.log("Načítavam počet registrovaných...");
        console.log("User status:", user ? "logged in" : "not logged in");

        const registrovaniResponse = await supabase.rpc(
          "ziskaj_pocet_vsetkych_pouzivatelov",
        );

        console.log("Response:", registrovaniResponse);

        if (registrovaniResponse.error) {
          throw new Error(`Direct count error: ${registrovaniResponse.error}`);
        }

        if (mounted) {
          const pocet = Number(registrovaniResponse.data) || 0;
          console.log("Data z DB:", registrovaniResponse.data);
          console.log("Typ dat:", typeof registrovaniResponse.data);
          console.log("Převedený počet:", pocet);
          console.log("Nastavujem počet registrovaných:", pocet);
          setPocetRegistrovanych(pocet);
        }
      } catch (err) {
        console.error("Chyba pri načítaní počtu registrovaných:", err);
      }
    };

    nacitajPocetRegistrovanych();

    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    let mounted = true;

    if (!vysledkyData && !agregovaneData) {
      const nacitajVysledky = async () => {
        setLoading(true);
        setError(null);
        const supabase = createClient();

        try {
          let vysledkyResponse;
          let pocetResponse;
          let agregovaneResponse;

          // Ak je viac hlasovaní, načítame agregované výsledky
          if (hlasovania.length > 1) {
            agregovaneResponse = await supabase.rpc(
              "ziskaj_agregovane_vysledky",
            );

            if (agregovaneResponse.error) {
              throw new Error(
                `ziskaj_agregovane_vysledky: ${agregovaneResponse.error.message}`,
              );
            }
          }
          // Ak je práve jedno hlasovanie, načítame konkrétne výsledky
          else if (hlasovania.length === 1) {
            vysledkyResponse = await supabase.rpc("ziskaj_vysledky", {
              p_hlasovanie_id: hlasovania[0].id,
            });

            if (vysledkyResponse.error) {
              throw new Error(
                `ziskaj_vysledky: ${vysledkyResponse.error.message}`,
              );
            }

            pocetResponse = await supabase.rpc("ziskaj_pocet_hlasov", {
              p_hlasovanie_id: hlasovania[0].id,
            });

            if (pocetResponse.error) {
              throw new Error(
                `get_pocet_hlasov: ${pocetResponse.error.message}`,
              );
            }
          }

          // Počet registrovaných používateľov načítame vždy
          const registrovaniResponse = await supabase.rpc(
            "ziskaj_pocet_vsetkych_pouzivatelov",
          );

          if (registrovaniResponse.error) {
            throw new Error(
              `ziskaj_pocet_vsetkych_pouzivatelov: ${registrovaniResponse.error.message}`,
            );
          }

          if (mounted) {
            if (hlasovania.length > 1) {
              // Pre viacero hlasovaní použijeme agregované dáta
              const data = {
                agregovane_vysledky: (agregovaneResponse?.data ||
                  []) as AgregovanyVysledok[],
                pocet_registrovanych: Number(registrovaniResponse.data) || 0,
              };
              setAgregovaneData(data);
            } else if (hlasovania.length === 1) {
              // Pre jedno hlasovanie použijeme konkrétne dáta
              const pocetHlasovValue =
                pocetResponse?.data &&
                Array.isArray(pocetResponse.data) &&
                pocetResponse.data[0]
                  ? (pocetResponse.data[0] as { celkovy_pocet: number })
                      .celkovy_pocet
                  : 0;
              const data = {
                vysledky: (vysledkyResponse?.data || []) as VysledokSlotu[],
                pocet_hlasov: Number(pocetHlasovValue) || 0,
                pocet_registrovanych: Number(registrovaniResponse.data) || 0,
              };
              setVysledkyData(data);
            }
            setLoading(false);
          }
        } catch (err) {
          const errorMsg =
            err instanceof Error
              ? err.message
              : "Nepodarilo sa načítať výsledky";
          if (mounted) {
            setError(errorMsg);
            setLoading(false);
          }
        }
      };

      nacitajVysledky();
    }

    return () => {
      mounted = false;
    };
  }, [hlasovania, vysledkyData, agregovaneData]);

  if (hlasovania.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 md:px-8 max-w-2xl">
        <Card className={EMPTY_STATE_CARD_CLASSES}>
          <CardHeader className="text-center">
            <div className={EMPTY_STATE_ICON_CONTAINER}>
              <Trophy className="h-8 w-8 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight">
              Žiadne hlasovania
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">
              Zatiaľ neboli vytvorené žiadne hlasovania.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (hlasovania.length > 1) {
    // Ak máme agregované dáta, zobrazíme ich
    if (agregovaneData) {
      return (
        <div className="min-h-screen bg-linear-to-br from-background via-background to-primary/5 dark:to-primary/10">
          <div className="container mx-auto px-4 py-12 md:px-8 max-w-7xl space-y-8">
            {user && (
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" size="sm" asChild className="gap-2">
                  <Link href="/">
                    <ArrowLeft className="h-4 w-4" />
                    Domov
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild className="gap-2">
                  <Link href={`/hlasovanie/${hlasovania[0].id}`}>
                    <Vote className="h-4 w-4" />
                    Hlasovať
                  </Link>
                </Button>
              </div>
            )}
            <VysledkyHeader
              nazov="Agregované výsledky všetkých hlasovaní"
              pocetVysledkov={agregovaneData.agregovane_vysledky.length}
              pocetRegistrovanych={agregovaneData.pocet_registrovanych}
            />

            <div className="flex justify-center">
              <div className={VIEW_SELECTOR_CONTAINER_CLASSES}>
                <Button
                  variant={viewType === "formations" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewType("formations")}
                  className={VIEW_BUTTON_CLASSES}
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">Formácie</span>
                </Button>
                <Button
                  variant={viewType === "rink" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewType("rink")}
                  className={VIEW_BUTTON_CLASSES}
                >
                  <MapIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Ihrisko</span>
                </Button>
                <Button
                  variant={viewType === "slots" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewType("slots")}
                  className={VIEW_BUTTON_CLASSES}
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">Sloty</span>
                </Button>
                <Button
                  variant={viewType === "roster" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewType("roster")}
                  className={VIEW_BUTTON_CLASSES}
                >
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Roster</span>
                </Button>
                <Button
                  variant={viewType === "analysis" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewType("analysis")}
                  className={VIEW_BUTTON_CLASSES}
                >
                  <BarChart3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Analýza</span>
                </Button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={viewType}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-12"
              >
                {viewType === "formations" && (
                  <RosterFormation
                    vysledky={agregovaneData.agregovane_vysledky}
                  />
                )}
                {viewType === "rink" && (
                  <RosterRink vysledky={agregovaneData.agregovane_vysledky} />
                )}
                {viewType === "slots" && (
                  <SlotsView vysledky={agregovaneData.agregovane_vysledky} />
                )}
                {viewType === "analysis" && (
                  <AnalysisView statistiky={analyzaStats} />
                )}
                {viewType === "roster" && (
                  <AgregatedRoster
                    vysledky={agregovaneData.agregovane_vysledky}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      );
    }

    // Ak ešte nemáme agregované dáta, zobrazíme zoznam hlasovaní
    return (
      <div className="min-h-screen bg-linear-to-br from-background via-background to-primary/5 dark:to-primary/10">
        <div className="container mx-auto px-4 py-8 md:px-8 max-w-6xl space-y-8">
          <VysledkyHeader
            nazov="Výsledky hlasovaní"
            pocetVysledkov={0}
            pocetRegistrovanych={pocetRegistrovanych}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {user && hlasovania.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Button variant="outline" size="sm" asChild className="gap-2">
                  <Link href="/">
                    <ArrowLeft className="h-4 w-4" />
                    Domov
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild className="gap-2">
                  <Link href={`/hlasovanie/${hlasovania[0].id}`}>
                    <Vote className="h-4 w-4" />
                    Hlasovať
                  </Link>
                </Button>
              </div>
            )}
            <Badge variant="outline" className="mb-4 px-3 py-1 w-fit sm:mb-6">
              <BarChart3 className="h-3 w-3 mr-1" />
              Výsledky hlasovaní
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight">
              Výsledky hlasovaní
            </h1>
            <p className="text-muted-foreground leading-relaxed max-w-2xl">
              Vyberte hlasovanie, ktorého výsledky chcete zobraziť.
            </p>
          </motion.div>
          <motion.div
            variants={containerAnimation}
            initial="hidden"
            animate="show"
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {hlasovania.map((h) => {
              const je_aktivne = isHlasovanieAktivne(h);
              return (
                <motion.div
                  key={h.id}
                  variants={itemAnimation}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="group border-muted/60 shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge
                          variant={je_aktivne ? "default" : "secondary"}
                          className={cn(
                            "font-medium shadow-none",
                            je_aktivne
                              ? "bg-status-success/15 text-status-success hover:bg-status-success/20 border-status-success/20"
                              : "",
                          )}
                        >
                          {je_aktivne ? (
                            <>
                              <Clock className="h-3 w-3 mr-1" />
                              Aktívne
                            </>
                          ) : (
                            "Ukončené"
                          )}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg group-hover:text-primary transition-colors">
                        {h.nazov}
                      </CardTitle>
                      {h.popis && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {h.popis}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4 mt-auto">
                      <div className="text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-4 w-4" />
                          <span>{formatDate(h.zaciatok)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Clock className="h-4 w-4" />
                          <span>{formatDate(h.koniec)}</span>
                        </div>
                      </div>
                      <Button
                        asChild
                        className="w-full rounded-full group-hover:shadow-md transition-all"
                      >
                        <Link href={`/vysledky/${h.id}`}>
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Zobraziť výsledky
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 md:px-8 max-w-5xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Načítavam výsledky...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 md:px-8 max-w-5xl">
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="text-center py-12 space-y-4">
            <p className="text-destructive font-bold">
              Chyba pri načítaní výsledkov
            </p>
            <p className="text-muted-foreground text-sm">{error}</p>
            <Button onClick={() => setError(null)} variant="outline">
              Skúsiť znova
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!vysledkyData) {
    return (
      <div className="container mx-auto px-4 py-12 md:px-8 max-w-5xl">
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">
              Nepodarilo sa načítať výsledky.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-background via-background to-primary/5 dark:to-primary/10">
      <div className="container mx-auto px-4 py-12 md:px-8 max-w-7xl space-y-8">
        {user && (
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" asChild className="gap-2">
              <Link href="/">
                <ArrowLeft className="h-4 w-4" />
                Domov
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild className="gap-2">
              <Link href={`/hlasovanie/${hlasovania[0].id}`}>
                <Vote className="h-4 w-4" />
                Hlasovať
              </Link>
            </Button>
          </div>
        )}
        <VysledkyHeader
          nazov={hlasovania[0].nazov}
          pocetVysledkov={agregovaneVysledky.length}
          pocetRegistrovanych={pocetRegistrovanych}
        />

        <div className="flex justify-center">
          <div className={VIEW_SELECTOR_CONTAINER_CLASSES}>
            <Button
              variant={viewType === "formations" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewType("formations")}
              className={VIEW_BUTTON_CLASSES}
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Formácie</span>
            </Button>
            <Button
              variant={viewType === "rink" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewType("rink")}
              className={VIEW_BUTTON_CLASSES}
            >
              <MapIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Ihrisko</span>
            </Button>
            <Button
              variant={viewType === "slots" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewType("slots")}
              className={VIEW_BUTTON_CLASSES}
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">Sloty</span>
            </Button>
            <Button
              variant={viewType === "roster" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewType("roster")}
              className={VIEW_BUTTON_CLASSES}
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Roster</span>
            </Button>
            <Button
              variant={viewType === "analysis" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewType("analysis")}
              className={VIEW_BUTTON_CLASSES}
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Analýza</span>
            </Button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={viewType}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-12"
          >
            {viewType === "formations" && (
              <RosterFormation vysledky={agregovaneVysledky} />
            )}
            {viewType === "rink" && (
              <RosterRink vysledky={agregovaneVysledky} />
            )}
            {viewType === "slots" && (
              <SlotsView vysledky={agregovaneVysledky} />
            )}
            {viewType === "analysis" && (
              <AnalysisView statistiky={analyzaStats} />
            )}
            {viewType === "roster" && (
              <AgregatedRoster vysledky={agregovaneVysledky} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
